import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";

interface HostOnboardingData {
  profile: boolean;
  payment: boolean;
  properties: boolean;
  verification: boolean;
  completed: boolean;
}

interface OnboardingStepsProps {
  currentStep: number;
  onboardingData: HostOnboardingData;
  onComplete: (step: keyof HostOnboardingData, completed: boolean) => void;
}

export const OnboardingSteps = ({ currentStep, onboardingData, onComplete }: OnboardingStepsProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleStepComplete = async (step: number) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Update the onboarding data in the profile
      const { error } = await supabase
        .from('profiles')
        .update({
          host_onboarding_step: step + 1,
          host_onboarding_data: {
            profile: step >= 1,
            payment: step >= 2,
            properties: step >= 3,
            verification: step >= 4,
            completed: step >= 4
          }
        })
        .eq('id', user.id);

      if (error) throw error;
      
      // Call the onComplete callback with the appropriate step
      let completedStep: keyof HostOnboardingData;
      switch(step) {
        case 1:
          completedStep = 'profile';
          break;
        case 2:
          completedStep = 'payment';
          break;
        case 3:
          completedStep = 'properties';
          break;
        case 4:
          completedStep = 'verification';
          break;
        default:
          throw new Error('Invalid step');
      }
      onComplete(completedStep, true);
    } catch (error) {
      console.error('Error updating onboarding step:', error);
      toast({
        title: "Error",
        description: "Failed to update onboarding progress",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="p-4 border rounded-lg">
        <h3 className="font-medium mb-2">Step 1: Complete Your Profile</h3>
        <p className="text-sm text-muted-foreground mb-4">Add your personal information and contact details</p>
        <Button 
          onClick={() => handleStepComplete(1)} 
          disabled={currentStep < 1}
        >
          {onboardingData.profile ? "Completed" : "Complete Profile"}
        </Button>
      </div>

      <div className="p-4 border rounded-lg">
        <h3 className="font-medium mb-2">Step 2: Set Up Payments</h3>
        <p className="text-sm text-muted-foreground mb-4">Add your payment methods and payout preferences</p>
        <Button 
          onClick={() => handleStepComplete(2)} 
          disabled={currentStep < 2}
        >
          {onboardingData.payment ? "Completed" : "Setup Payments"}
        </Button>
      </div>

      <div className="p-4 border rounded-lg">
        <h3 className="font-medium mb-2">Step 3: Add Properties</h3>
        <p className="text-sm text-muted-foreground mb-4">List your first property on our platform</p>
        <Button 
          onClick={() => handleStepComplete(3)} 
          disabled={currentStep < 3}
        >
          {onboardingData.properties ? "Completed" : "Add Properties"}
        </Button>
      </div>

      <div className="p-4 border rounded-lg">
        <h3 className="font-medium mb-2">Step 4: Verify Identity</h3>
        <p className="text-sm text-muted-foreground mb-4">Complete identity verification for trust and safety</p>
        <Button 
          onClick={() => handleStepComplete(4)} 
          disabled={currentStep < 4}
        >
          {onboardingData.verification ? "Completed" : "Verify Identity"}
        </Button>
      </div>
    </div>
  );
};